import { gql } from '@apollo/client';
import type { TypedDocumentNode } from '@apollo/client';
import classNames from 'classnames';
import type { PropsWithChildren } from 'react';
import { useState } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import { ManagerProgressionTaskState } from '@sorare/core/src/__generated__/globalTypes';
import { namedOperations } from '@sorare/core/src/__generated__/operations';
import { LoadingButton } from '@sorare/core/src/atoms/buttons/LoadingButton';
import { ChevronDoubleUp } from '@sorare/core/src/atoms/icons/ChevronDoubleUp';
import { Locker } from '@sorare/core/src/atoms/icons/Locker';
import { Horizontal, Vertical } from '@sorare/core/src/atoms/layout/flex';
import { BrandXXS, LabelS } from '@sorare/core/src/atoms/typography';
import { useMutation } from '@sorare/core/src/hooks/graphql/useMutation';
import { glossary } from '@sorare/core/src/lib/glossary';
import { isType } from '@sorare/core/src/lib/gql';

import { RevealPackCardsDialogFromReward } from 'components/commonCard/RevealPackCardsDialogFromReward';

import { StarPackProgress } from '../StarPackProgress';
import type {
  MarketClaimStarPackMutation,
  MarketClaimStarPackMutationVariables,
  StarPackContent_cardPack,
} from './__generated__/index.graphql';

const PackImgWrapper = styled.div`
  position: relative;
`;
const PackImg = styled.img`
  width: calc(18 * var(--unit));
  object-fit: contain;
  height: 100%;

  &.disabled {
    filter: brightness(0.3);
  }
`;
const OddsWrapper = styled(Horizontal).attrs({ gap: 0 })`
  color: var(--c-black);
  background: var(--c-gradient-greenYellow);
  position: relative;
  top: var(--half-unit);
  min-width: calc(100% + var(--intermediate-unit));
  text-wrap: nowrap;
  padding: var(--half-unit);
  border-radius: var(--half-unit) var(--half-unit) var(--half-unit) 0;

  &::before {
    content: '';
    position: absolute;
    left: 0;
    bottom: 0;
    transform: translateY(100%);
    width: 0;
    height: 0;
    border-left: var(--double-and-a-half-unit) solid transparent;
    border-right: var(--double-and-a-half-unit) solid transparent;
    border-top: var(--double-and-a-half-unit) solid #0f6636;
    z-index: -1;
  }
`;
const StarPlayerWrapper = styled(Vertical).attrs({ center: true, gap: 0 })`
  position: absolute;
  bottom: 0;
  right: 0;
  transform: translateX(50%);
  width: calc(11 * var(--unit));

  & > img {
    width: 100%;
    aspect-ratio: var(--card-aspect-ratio);
  }
`;
const StyledLoadingButton = styled(LoadingButton)`
  width: 100%;
`;

const MARKET_CLAIM_STAR_PACK_MUTATION = gql`
  mutation MarketClaimStarPackMutation($taskId: ID!) {
    claimTask(input: { taskId: $taskId }) {
      task {
        id
        aasmState
        ... on StarPackTask {
          id
          progress
        }
      }
      rewards {
        id
        ...RevealPackCardsDialogFromReward_anyRewardInterface
      }
      errors {
        message
        code
      }
    }
  }
  ${RevealPackCardsDialogFromReward.fragments.anyRewardInterface}
` as TypedDocumentNode<
  MarketClaimStarPackMutation,
  MarketClaimStarPackMutationVariables
>;

type Props = {
  cardPack: StarPackContent_cardPack;
};

export const StarPackContent = ({
  cardPack,
  children,
}: PropsWithChildren<Props>) => {
  const [claimMutationData, setClaimMutationData] =
    useState<MarketClaimStarPackMutation['claimTask']>();

  const [claimStarPackTask, { loading }] = useMutation(
    MARKET_CLAIM_STAR_PACK_MUTATION,
    {
      showErrorsWithSnackNotification: true,
      // refetch to get updated task and pack odds
      refetchQueries: [namedOperations.Query.CommonPacksQuery],
    }
  );
  const { pictureUrl: packPictureUrl, primaryColor, rewardedByTask } = cardPack;
  if (!rewardedByTask || !isType(rewardedByTask, 'StarPackTask')) {
    return null;
  }
  const { progress, target, starPlayerOddsBasisPoint, starPlayer, aasmState } =
    rewardedByTask;

  const onClaim = async () => {
    const { data } = await claimStarPackTask({
      variables: {
        taskId: rewardedByTask.id,
      },
    });
    setClaimMutationData(data?.claimTask);
  };

  const {
    pictureUrl: starPlayerPictureUrl,
    anyPlayer: { displayName: starPlayerDisplayName },
  } = starPlayer;
  const claimable = aasmState === ManagerProgressionTaskState.COMPLETED;
  return (
    <>
      <Vertical center gap={0.5}>
        <PackImgWrapper>
          <PackImg
            src={packPictureUrl}
            alt=""
            className={classNames({ disabled: !claimable })}
          />
          {!!starPlayerPictureUrl && (
            <StarPlayerWrapper>
              <OddsWrapper center>
                <ChevronDoubleUp size={1.5} />
                <BrandXXS color="var(--c-black)">
                  <FormattedMessage
                    id="StarPack.starPlayerOdds"
                    defaultMessage="{starPlayerOdds, number, percent} chance"
                    values={{
                      starPlayerOdds: starPlayerOddsBasisPoint / 10000,
                    }}
                  />
                </BrandXXS>
              </OddsWrapper>
              <img alt={starPlayerDisplayName} src={starPlayerPictureUrl} />
            </StarPlayerWrapper>
          )}
        </PackImgWrapper>
        {claimable ? (
          <StyledLoadingButton
            loading={loading}
            size="small"
            color="primary"
            onClick={onClaim}
          >
            <FormattedMessage {...glossary.claim} />
          </StyledLoadingButton>
        ) : (
          <Vertical center>
            <Vertical center gap={0.5}>
              <Locker />
            </Vertical>
            <LabelS>
              <FormattedMessage
                id="StarPack.instructions"
                defaultMessage="Open packs. Elites count double"
              />
            </LabelS>
            <StarPackProgress
              progress={progress}
              target={target}
              color={primaryColor}
            />
          </Vertical>
        )}
        {children}
      </Vertical>
      <RevealPackCardsDialogFromReward
        open={!!claimMutationData}
        reward={claimMutationData?.rewards?.at(0)}
        onClose={() => {
          setClaimMutationData(undefined);
        }}
      />
    </>
  );
};

StarPackContent.fragments = {
  cardPack: gql`
    fragment StarPackContent_cardPack on CardPack {
      slug
      pictureUrl
      primaryColor: color(variant: PRIMARY)
      rewardedByTask {
        id
        aasmState
        ... on StarPackTask {
          id
          progress
          target
          starPlayerOddsBasisPoint
          starPlayer {
            id
            pictureUrl
            anyPlayer {
              slug
              displayName
            }
          }
        }
      }
    }
  ` as TypedDocumentNode<StarPackContent_cardPack>,
};
